"use client";

import { formatDistanceToNow } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface CommentProps {
  id: string;
  author: {
    name: string;
    avatar?: string;
    role?: string;
  };
  content: string;
  createdAt: Date;
  bordered?: boolean;
  onDelete?: () => Promise<void>;
}

export function Comment({
  author,
  content,
  createdAt,
  bordered = true,
  onDelete,
}: CommentProps) {
  return (
    <Card
      className={cn(
        // "w-full max-w-3xl mx-auto mb-4",
        "w-full mb-4",
        bordered ? "shadow-sm" : "shadow-none border-none"
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between p-4">
        <div className="flex items-center gap-4">
          {author.avatar ? (
            <Avatar className="w-10 h-10">
              <AvatarImage src={author.avatar} alt={author.name} />
              <AvatarFallback>{author.name.charAt(0)}</AvatarFallback>
            </Avatar>
          ) : (
            <div className="flex items-center justify-center w-10 h-10 bg-gray-200 rounded-full">
              <span className="font-semibold text-gray-600">
                {author.name.charAt(0)}
              </span>
            </div>
          )}
          <div className="flex flex-col">
            <p className="text-sm font-semibold">{author.name}</p>
            {author.role && (
              <p className="text-xs text-gray-500">{author.role}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-500">
            {formatDistanceToNow(createdAt, { addSuffix: true })}
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              style={{
                zIndex: 1000,
              }}
            >
              {onDelete && (
                <DropdownMenuItem className="text-red-600" onClick={onDelete}>
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <p className="text-sm">{content}</p>
      </CardContent>
    </Card>
  );
}
